import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { ReactComponent as ProfileIcon } from "../../icons/profile icon.svg";
import { ReactComponent as SearchIcon } from "../../icons/search icon.svg";
import { ReactComponent as CartIcon } from "../../icons/cart icon.svg";
import { ReactComponent as CallIcon } from "../../icons/call icon.svg";
import { ReactComponent as MailIcon } from "../../icons/mail icon.svg";
import googlePlayBadge from "../../icons/Google Play.svg";
import appStoreBadge from "../../icons/App store.svg";
import { resetCart } from "../../features/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../features/authSlice";
import { persistor } from "../../app/store";
import { resetSubscription } from "../../features/subscriptionSlice";
import { clearWishlistLocal } from "../../features/wishlistSlice";
import LoginPromptModal from "../../components/home/authComponents/LoginPromptModal";
import { jwtDecode } from "jwt-decode";
import ExitIntentPopup from "../common/ExitIntentPopup";
import CouponManager from "../../services/dashboard/CouponManager";
import { useExitIntent } from "../../hooks/useExitIntent";
import config from "../../config";

const Header = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingLogout, setPendingLogout] = useState(false);

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const cartItems = useSelector((state) => state.cart.items) || [];
  const wishlistItems = useSelector((state) => state.wishlist.items) || [];
  const subscription = useSelector((state) => state.subscription.subscription);
  const userId = useSelector((state) => state.auth.user?.userId);

  const cartCount = cartItems.length;
  const wishlistCount = wishlistItems.length;

  // Exit Intent Configuration - Fetch actual coupon data from backend
  const [exitIntentCoupon, setExitIntentCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(true);
  const [hasSubscriptionHistory, setHasSubscriptionHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Check if user has active subscription
  const hasActiveSubscription = subscription && subscription.status === 'ACTIVE';

  // Fetch exit intent coupon from backend
  useEffect(() => {
    const fetchExitIntentCoupon = async () => {
      console.log("🔍 Starting coupon fetch...");
      console.log("Is authenticated:", isAuthenticated);
      
      if (!isAuthenticated) {
        console.log("⏭️ Skipping coupon fetch - not authenticated");
        return;
      }

      try {
        setCouponLoading(true);
        console.log("📡 Calling API: /api/coupons/type/NEW_SUBSCRIPTION");
        
        // Fetch NEW_SUBSCRIPTION type coupons
        const response = await CouponManager.getCouponsByType("NEW_SUBSCRIPTION");
        
        console.log("📋 API Response:", response);
        console.log("📋 Fetched coupons:", response?.responseData);
        
        // Find WELCOME100 coupon specifically (case-insensitive)
        const activeCoupon = response?.responseData?.find(
          (coupon) => coupon.status === "ACTIVE" && 
                     coupon.couponCode?.toUpperCase() === "WELCOME100"
        );

        console.log("🎫 WELCOME100 coupon found:", activeCoupon);

        if (activeCoupon) {
          // Check if coupon is expired
          const expiryDate = activeCoupon.expiryDate;
          let isExpired = false;
          
          if (expiryDate && Array.isArray(expiryDate) && expiryDate.length >= 5) {
            const expiryDateTime = new Date(
              expiryDate[0], // year
              expiryDate[1] - 1, // month (convert to 0-indexed)
              expiryDate[2], // day
              expiryDate[3] || 0, // hour
              expiryDate[4] || 0, // minute
              expiryDate[5] || 0  // second (optional)
            );
            
            const now = new Date();
            isExpired = expiryDateTime.getTime() <= now.getTime();
            
            console.log("📅 Coupon expiry check:");
            console.log("  - Expiry date:", expiryDateTime.toString());
            console.log("  - Current date:", now.toString());
            console.log("  - Is expired:", isExpired);
          }
          
          // Only set coupon if it's NOT expired
          if (!isExpired) {
            const couponData = {
              code: activeCoupon.couponCode,
              discountAmount: activeCoupon.discount,
              isActive: activeCoupon.status === "ACTIVE",
              isFlatDiscount: activeCoupon.isFlatDiscount || false,
              expiryDate: activeCoupon.expiryDate, // [year, month, day, hour, minute, second, nano]
            };
            console.log("✅ Coupon data set:", couponData);
            setExitIntentCoupon(couponData);
          } else {
            console.log("❌ WELCOME100 coupon has expired - will not show popup");
            setExitIntentCoupon(null);
          }
        } else {
          console.log("❌ WELCOME100 coupon not found or not active");
          console.log("Available coupons:", response?.responseData);
        }
      } catch (error) {
        console.error("❌ Failed to fetch exit intent coupon:", error);
        console.error("Error details:", error.response || error.message);
      } finally {
        setCouponLoading(false);
        console.log("✅ Coupon loading complete");
      }
    };

    fetchExitIntentCoupon();
  }, [isAuthenticated]);

  // Check if user has ever subscribed (for exit intent eligibility)
  useEffect(() => {
    const checkSubscriptionHistory = async () => {
      console.log("🔍 Checking subscription history...");
      
      if (!isAuthenticated || !userId) {
        console.log("⏭️ Skipping history check - not authenticated or no userId");
        setHasSubscriptionHistory(false); // Default to false (eligible) when not authenticated
        setHistoryLoading(false);
        return;
      }

      try {
        setHistoryLoading(true);
        console.log(`📡 Calling API: /api/subscriptions/user/${userId}/has-subscription-history`);
        
        const response = await fetch(`${config.url}/api/subscriptions/user/${userId}/has-subscription-history`);
        
        if (!response.ok) {
          console.error("❌ API request failed with status:", response.status);
          // On network error, assume user is NEW (false) - don't block popup unnecessarily
          setHasSubscriptionHistory(false);
          setHistoryLoading(false);
          return;
        }
        
        const data = await response.json();
        console.log("📋 Subscription history response:", data);
        
        if (data.responseCode === 200) {
          const hasHistory = data.responseData;
          console.log(hasHistory ? "❌ User has subscription history - won't show popup" : "✅ User is new - eligible for popup");
          setHasSubscriptionHistory(hasHistory);
        } else {
          console.error("❌ Failed to check subscription history:", data.responseMessage);
          // On backend error, assume user is NEW (false) - don't block popup unnecessarily
          setHasSubscriptionHistory(false);
        }
      } catch (error) {
        console.error("❌ Error checking subscription history:", error);
        // On exception, assume user is NEW (false) - don't block popup unnecessarily
        setHasSubscriptionHistory(false);
      } finally {
        setHistoryLoading(false);
        console.log("✅ History check complete - hasSubscriptionHistory:", hasSubscriptionHistory);
      }
    };

    checkSubscriptionHistory();
  }, [isAuthenticated, userId]);

  // Use exit intent hook for auto-trigger after 3 minutes + mouse exit detection
  const { showExitPopup: autoShowExitPopup, setShowExitPopup: setAutoShowExitPopup } = useExitIntent(
    isAuthenticated,
    hasActiveSubscription,
    exitIntentCoupon,
    hasSubscriptionHistory
  );

  // Manual exit popup state (for logout button)
  const [manualShowExitPopup, setManualShowExitPopup] = useState(false);

  // Combined popup state - show if either auto or manual trigger is active
  const showExitPopup = autoShowExitPopup || manualShowExitPopup;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (token) {
      try {
        const { exp } = jwtDecode(token);
        const isExpired = Date.now() >= exp * 1000;

        if (isExpired) {
          dispatch(resetCart());
          dispatch(resetSubscription());
          dispatch(clearWishlistLocal());
          dispatch(logout());
          persistor.purge();
          localStorage.removeItem("authToken");
          navigate("/login");
        }
      } catch (err) {
        console.error("Token decode error:", err);
        dispatch(logout());
        localStorage.removeItem("authToken");
        navigate("/login");
      }
    }
  }, [dispatch, navigate]);

  const performLogout = () => {
    persistor.purge();
    dispatch(resetCart());
    dispatch(resetSubscription());
    dispatch(clearWishlistLocal());
    dispatch(logout());
    navigate("/login");
  };

  const handleLogout = () => {
    console.log("=== LOGOUT CLICKED ===");
    console.log("Exit intent coupon:", exitIntentCoupon);
    console.log("Has active subscription:", hasActiveSubscription);
    console.log("Is authenticated:", isAuthenticated);
    console.log("Subscription object:", subscription);
    console.log("Coupon loading:", couponLoading);
    console.log("History loading:", historyLoading);
    console.log("Has subscription history:", hasSubscriptionHistory);
    console.log("Auto show exit popup:", autoShowExitPopup);
    console.log("Manual show exit popup:", manualShowExitPopup);
    
    // If data is still loading, wait briefly then proceed anyway
    if (couponLoading || historyLoading) {
      console.log("⏳ Still loading data - will wait briefly...");
      // Wait 1 second max for data to load, then proceed
      setTimeout(() => {
        console.log("⏱️ Done waiting - proceeding with eligibility check");
        checkEligibilityAndProceed();
      }, 1000);
      return;
    }
    
    // If not loading, check eligibility immediately
    checkEligibilityAndProceed();
  };

  const checkEligibilityAndProceed = () => {
    console.log("🔍 Checking exit popup eligibility...");
    console.log("  - isAuthenticated:", isAuthenticated);
    console.log("  - hasActiveSubscription:", hasActiveSubscription);
    console.log("  - exitIntentCoupon:", exitIntentCoupon ? `Found: ${exitIntentCoupon.code}` : "null");
    console.log("  - hasSubscriptionHistory:", hasSubscriptionHistory);
    
    // Check if user should see exit intent popup
    // Must have: no active subscription, user is authenticated, coupon is loaded, and user has NEVER subscribed
    if (isAuthenticated && !hasActiveSubscription && exitIntentCoupon && !hasSubscriptionHistory) {
      console.log("✅ All conditions met - showing popup!");
      setPendingLogout(true);
      setManualShowExitPopup(true);
      return; // Don't logout yet
    }
    
    // Debug why popup is not showing
    console.log("❌ Popup not eligible because:");
    if (!isAuthenticated) {
      console.log("  - User not authenticated");
    }
    if (hasActiveSubscription) {
      console.log("  - User has active subscription");
    }
    if (!exitIntentCoupon) {
      console.log("  - No coupon loaded");
    }
    if (hasSubscriptionHistory) {
      console.log("  - User has subscription history (not a new user)");
    }
    
    console.log("🚪 Logging out immediately (no popup)");
    // If no popup should show, logout immediately
    performLogout();
  };

  const handleExitPopupClose = () => {
    console.log("🔴 Close button (X) clicked - canceling logout");
    // Close both auto and manual popup triggers
    setAutoShowExitPopup(false);
    setManualShowExitPopup(false);
    // Cancel pending logout - user dismissed the popup
    setPendingLogout(false);
  };

  const handleContinueLogout = () => {
    console.log("✅ Continue Logout clicked - proceeding with logout");
    // Close popups
    setAutoShowExitPopup(false);
    setManualShowExitPopup(false);
    // Proceed with logout
    setTimeout(() => {
      performLogout();
      setPendingLogout(false);
    }, 300);
  };

  const handleProceedToPlans = () => {
    console.log("🎁 Claim Offer clicked - canceling logout");
    // User wants to claim offer - cancel logout and close popup
    setPendingLogout(false);
    setAutoShowExitPopup(false);
    setManualShowExitPopup(false);
  };

  return (
    <header className="bg-primary text-white text-sm sticky top-0 z-[9999]">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col gap-2 sm:flex-row items-center justify-between">
        {/* Contact Info */}
        <div className="hidden sm:flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
          <div className="flex items-center gap-1">
            <CallIcon className="h-4 w-4" />
            <a href="tel:+919860133100" className="text-white hover:text-gray">
              +91 9860133100
            </a>
            <span className="text-white/70">/</span>
            <a href="tel:+918788734240" className="text-white hover:text-gray">
              +91 8788734240
            </a>
          </div>
          <span className="hidden sm:block">|</span>
          <div className="flex items-center gap-1">
            <MailIcon className="h-5 w-5" />
            <a
              href="mailto:care@booksatdoorsteps.com?subject=Customer%20Service%20Inquiry"
              className="leading-none"
            >
              care@booksatdoorsteps.com
            </a>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex flex-wrap items-center justify-center sm:justify-end gap-5 sm:gap-6 text-center sm:text-right">
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline">Download Our App</span>

            <a
              href="https://play.google.com/store/apps/details?id=com.applauseitsolutions.booksatdoorsteps"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={googlePlayBadge}
                alt="Google Play"
                className="h-7 object-contain"
              />
            </a>

            <a
              href="https://apps.apple.com/in/app/booksatdoorsteps/id6478926787"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={appStoreBadge}
                alt="App Store"
                className="h-7 object-contain"
              />
            </a>
          </div>

          <span className="hidden sm:block">|</span>

          <div
            className="flex items-center gap-3 relative z-50"
            ref={dropdownRef}
          >
            <ProfileIcon
              className="h-5 w-5 cursor-pointer hover:opacity-80"
              onClick={() => setShowDropdown((prev) => !prev)}
            />

            {showDropdown && (
              <div className="absolute right-0 top-8 w-44 bg-white text-gray-800 rounded-xl shadow-xl z-50 border border-gray-200 animate-fade-in">
                {!isAuthenticated ? (
                  <Link
                    to="/login"
                    className="block text-left px-5 py-3 text-sm font-medium rounded-t-xl hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                    onClick={() => setShowDropdown(false)}
                  >
                    Login
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/profile"
                      className="block text-left px-5 py-3 text-sm font-medium hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setShowDropdown(false)}
                    >
                      My Profile
                    </Link>

                    <Link
                      to="/myOrders"
                      className="block text-left px-5 py-3 text-sm font-medium hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setShowDropdown(false)}
                    >
                      My Orders
                    </Link>

                    <Link
                      to="/subscription-details"
                      className="block text-left px-5 py-3 text-sm font-medium hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setShowDropdown(false)}
                    >
                      Subscriptions
                    </Link>

                    <Link
                      to="/books-with-me"
                      className="block text-left px-5 py-3 text-sm font-medium hover:bg-gray-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setShowDropdown(false)}
                    >
                      Books With Me
                    </Link>

                    <div className="border-t border-gray-100 my-1 mx-2" />

                    <button
                      onClick={() => {
                        handleLogout();
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-5 py-3 text-sm font-medium rounded-b-xl hover:bg-gray-50 hover:text-red-500 transition-all duration-200"
                    >
                      Logout
                    </button>
                  </>
                )}
              </div>
            )}

            <Link to="/search">
              <SearchIcon className="h-6 w-6 cursor-pointer hover:opacity-80" />
            </Link>

            <button
              onClick={() =>
                isAuthenticated
                  ? navigate("/wishlist")
                  : setShowLoginModal(true)
              }
              className="relative"
            >
              <Heart className="h-5 w-5 cursor-pointer hover:opacity-80" />
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>

            <button
              onClick={() =>
                isAuthenticated ? navigate("/cart") : setShowLoginModal(true)
              }
              className="relative"
            >
              <CartIcon className="h-5 w-5 cursor-pointer hover:opacity-80" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      {/* Exit Intent Popup - Always render when showExitPopup is true */}
      {showExitPopup && exitIntentCoupon && (
        <ExitIntentPopup
          isOpen={showExitPopup}
          onClose={handleExitPopupClose}
          couponCode={exitIntentCoupon.code}
          discountAmount={exitIntentCoupon.discountAmount}
          isFlatDiscount={exitIntentCoupon.isFlatDiscount}
          expiryDate={exitIntentCoupon.expiryDate}
          onProceedToPlans={handleProceedToPlans}
          onContinueLogout={handleContinueLogout}
        />
      )}
    </header>
  );
};

export default Header;
